import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { CreateNewIcon, DeleteIcon } from "../../../../../connection/channel-icons";
import { UploadFile } from "../../../../../connection/uploads";
import { updateChannelIcon, updateHomepageMainNav } from "../../../../../store/actions/admin/create-event";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { ChannelCustomizedIcon } from "../../../../../types/working-model";
import LargeButton from "../../../../general-ui/button/large-button";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import Modal from "../../../../general-ui/modal/modal";
import './channel-custom-icon.scss';

interface Props {
	displayUploadImageModal: boolean,
	setDisplayUploadImageModal: React.Dispatch<React.SetStateAction<boolean>>,
	nameNavItem?: string,
	itemUuid?: string,
	setIsCustomIcon: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}

const ChannelCustomizedIconModal = ({ displayUploadImageModal, setDisplayUploadImageModal, itemUuid, setIsCustomIcon }: Props) => {


	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const iconsImageArray = useTypedSelector(state => state.CreateEventReducer.channelIcons);

	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const channel = user?.active_channel;

	const [uploading, setUploading] = useState(false);
	const [uploadedImage, setUploadedImage] = useState(false);
	const [uploadedError, setUploadedError] = useState("");
	const [uploadImageModal, setUpLoadImageModal] = useState(false);
	const [currentIcon, setCurrentIcon] = useState("");
	const [icon, setIcon] = useState<ChannelCustomizedIcon>();
	const [deleteError, setDeleteError] = useState("");
	const [deletedModal, setDeleteModal] = useState(false);

	const dispatch = useDispatch();

	//Upload Image to s3 bucket
	const uploadImage = async (imageFile: File) => {
		if (imageFile instanceof File) {
			setUploading(true);
			try {
				if (channel && user && token) {
					const uploadedImageURL = await UploadFile(user, token, imageFile);
					const newIcon = {
						channel: channel,
						name: imageFile.name,
						url: uploadedImageURL
					};
					setIcon(newIcon);
					setUploadedImage(true);
					setUploadedError("");
				}
			} catch (e: any) {
				console.error(e);
				setUploadedError("We ran into an issue uploading the new icon. Please try again.");
				setUploadedImage(false);
			}
			setUploading(false);
		}
	};

	//Update the channel icon list in redux without saving to the database since we already have the query to pull in the data for channel icon list in the initial loading.
	const handleAddBtn = async () => {
		if (user && token) {
			try {
				//create a new channel icon in the database and return the uuid.
				const res = await CreateNewIcon(token, icon as ChannelCustomizedIcon);
				const _icon = { ...icon, uuid: res.uuid };

				//add the new icon to the list of icons.
				const clonedArray: ChannelCustomizedIcon[] = [...(iconsImageArray || []), _icon as ChannelCustomizedIcon];

				if (iconsImageArray) {
					dispatch(updateChannelIcon(clonedArray)); //Update the channel icon list in redux
				}

				setDisplayUploadImageModal(false);
				setUploading(false);
				setUploadedError("");
			} catch (error) {
				console.error(error);
				setUploadedError("We ran into an issue creating the new icon. Please try again.");
				setDisplayUploadImageModal(true);
			}
		}
	};

	//Make sure the user uploads the svg file and the size is in square sized. 
	const handleFile = async (file: File | FileList) => {
		if (file instanceof File) {
			const reader = new FileReader();
			reader.readAsText(file);
			reader.onload = () => {
				const svgText = reader.result;
				if (typeof svgText === 'string') {
					const doc = new DOMParser().parseFromString(svgText, 'application/xml');
					/* UNKNOWN TYPE here is because I have to check the viewbox of the uploaded svg to make sure it is in square size. So I parsed the file as text, but unfortanely I got another error "Type 'HTMLElement' is missing the following properties from type 'SVGSVGElement'".So that s I converted the type to unknow first and converted it to SVGSVElement to get the viewBox. */
					const svg = doc.documentElement as unknown as SVGSVGElement;
					if (svg.viewBox) {
						const viewBoxHeight = svg.viewBox.baseVal.height;
						const viewBoxWidth = svg.viewBox.baseVal.width;
						if (viewBoxHeight !== viewBoxWidth) {
							setUploadedError("Your image is not in the square size.");
						}
						else {
							uploadImage(file); // If the img is in the correct format and has a square size, we call the uploadImage function to upload that img to the s3 bucket.
						}
					}
				}
			};
		}
	};

	// This is called when the user selects the img in the current channel icon list instead of uploading the new icon. 
	// We update the specific's main nav items event here, so the newest icon will not impact other events. 
	const addCurrentIconToWorkingEvent = () => {
		const eventMainNavItems = workingEvent?.homepage?.event_main_nav_items;
		//currentIcon stores the uuid of the new icon.
		if (currentIcon && eventMainNavItems) {
			const _mainNavItems = eventMainNavItems.map((ni) => {
				if (ni.uuid === itemUuid) {
					return {
						...ni,
						custom_icon_uuid: currentIcon,
						is_custom: true
					};
				}
				return ni;
			});
			//update the main nav item with the custom icon.
			dispatch(updateHomepageMainNav(_mainNavItems));
			setIsCustomIcon(true);
		}
		setUpLoadImageModal(false);
		setDisplayUploadImageModal(false);
	};

	//Take the list of custom icon user uploaded - channel wise and display them.
	const renderIcons = useMemo(() => {
		return iconsImageArray?.map((iconImg) => {
			if (iconImg)
				return (
					<div key={iconImg?.uuid} onClick={() => setCurrentIcon(iconImg?.uuid as string)} className={classNames("custom-icon", { selected: currentIcon === iconImg?.uuid })}>
						<img src={iconImg?.url} title={iconImg?.name} />
						<button className="delete-icon" onClick={() => setDeleteModal(true)}><Icon name={ICONS.TRASH} size={16} color={COLORS.WHITE} /></button>
					</div >
				);
		});
	}, [currentIcon, iconsImageArray]);

	// If the user does not like using the custom icon anymore, we reset it here.
	// Home => return back the little home icon.
	// Agenda/Sessions => return back the calendar icon.
	const resetIcon = () => {
		const eventMainNavItems = workingEvent?.homepage?.event_main_nav_items;
		if (eventMainNavItems) {
			const _mainNavItems = eventMainNavItems.map((ni) => {
				if (ni.uuid === itemUuid) {
					return {
						...ni,
						is_custom: false
					};
				}
				return ni;
			});
			setIsCustomIcon(false);
			dispatch(updateHomepageMainNav(_mainNavItems));
		}
		setUpLoadImageModal(false);
		setDisplayUploadImageModal(false);
	};

	//Delete an icon in the list
	const handleDeleteIcon = async () => {
		if (token && channel) {
			const res = await DeleteIcon(token, currentIcon);
			if (res) {
				setDeleteError("");
				setDeleteModal(false);

				if (iconsImageArray) {
					const clonedArray: ChannelCustomizedIcon[] = iconsImageArray.map(icon => {
						if (icon.uuid === currentIcon) {
							const deletedIcon = {
								...icon,
								is_custom: false,
								status: 0
							};
							return deletedIcon;
						}
						return icon;
					}).filter(deletedIcon => deletedIcon.uuid !== currentIcon);

					dispatch(updateChannelIcon(clonedArray));
					setIsCustomIcon(false);
				}
			}
			else {
				setDeleteError("We ran into issue of deleting the icon. Please try again!");
				setDeleteModal(true);
			}
		}
	};


	return (
		<>
			<Modal
				title={`Choose Custom Icon`}
				onRequestClose={() => setDisplayUploadImageModal(false)}
				open={displayUploadImageModal}
				closeable={false}
				className={"upload-icon-modal"}
				cancellable
				size="medium"
				footer={
					<div className="add-modal-buttons">
						{!uploadImageModal && (iconsImageArray && iconsImageArray.length > 0) ? <button className="no-style restore-default text-only-primary" onClick={() => setUpLoadImageModal(true)}>Upload custom icon image</button> : <div></div>}
						<button onClick={() => setDisplayUploadImageModal(false)}>Cancel</button>
						{(!uploadImageModal && iconsImageArray && iconsImageArray.length > 0) ? <button className="primary" onClick={addCurrentIconToWorkingEvent} disabled={currentIcon === ""}>Choose your icon</button> : <button className="primary" onClick={handleAddBtn} disabled={!uploadedImage || uploadedError.length > 0}>Add</button>}
					</div>
				}>

				{!uploadImageModal ? <button className="no-style text-only-primary restore-icon" onClick={resetIcon}>Restore to the default icon</button> : <div></div>}

				{!uploadImageModal && (iconsImageArray && iconsImageArray.length > 0) ?
					<div className={classNames(`channel-icon-container ${iconsImageArray && iconsImageArray.length >= 4 ? `more-than-three-icons` : ``}`)}>
						{renderIcons}
					</div>
					:
					<>
						<LargeButton
							title={`Upload Image`}
							subtitle={"Only SVG and square frame size. We recommend an image that is 24 by 24, in width and height."}
							allowedFileTypes={['image/svg+xml']}
							uploading={uploading}
							onFile={handleFile}
						/>
						<p className="square-size-error">{uploadedError}</p>
					</>
				}
			</Modal>


			<Modal
				title={`Delete Icon`}
				onRequestClose={() => setDeleteModal(false)}
				open={deletedModal}
				closeable={false}
				cancellable
				size="small"
				footer={
					<>
						<button onClick={() => setDeleteModal(false)}>Cancel</button>
						<button className="primary" onClick={() => handleDeleteIcon()}>Delete</button>
					</>
				}>
				<p>Are you sure you want to delete this icon? </p>
				<p className="square-size-error">{deleteError}</p>
			</Modal>
		</>
	);
};

export default ChannelCustomizedIconModal;
